import { Component, OnInit, Input, ComponentRef, OnDestroy } from '@angular/core';
import { merge, Observable } from 'rxjs';
import { IWidget } from '../../../interfaces/iWidget';
import { AwallComponentEvent } from '../../../models/awallComponentEvent';
import { DrawnDependency } from '../../../models/drawnDependency';
import { ComponentEvent } from '../../../models/enums/componentEvent';
import { Criticality } from '../../../models/enums/criticality';
import { IssueType } from '../../../models/enums/issueType';
import { Issue } from '../../../models/issue';
import { IssueLink } from '../../../models/issueLink';
import { Person } from '../../../models/person';
import { Status } from '../../../models/status';
import { ActiveBoardService } from '../../../services/active-board.service';
import { DependencyDrawingService } from '../../../services/dependency-drawing.service';
import { DependencyEditmodeService } from '../../../services/dependency-editmode.service';
import { PersonService } from '../../../services/person.service';
import { IssueTypeMappingUtil } from '../../../util/issuetype-mapping-util';
import { Listener, CommunicationService } from '../../../services/communication.service';

@Component({
  selector: 'app-filter-widget',
  templateUrl: './filter-widget.component.html',
  styleUrls: ['./filter-widget.component.scss']
  })
export class FilterWidgetComponent implements IWidget, OnInit, OnDestroy, Listener<AwallComponentEvent> {
  public static widgetName = 'issueFilter';
  public static readonly componentId = 'FilterWidgetComponent';
  @Input() widget = true;

  statuses: Status[] = [];
  members = new Map<string, Person>();
  criticalities = Object.keys(Criticality);
  issueTypes: Map<string, string> = IssueTypeMappingUtil.mapToJiraIssueTypeMap(IssueType);
  filteredStatuses = new Set<number>();
  filteredAssignees = new Set<string>();
  filteredTypes = new Set<string>();
  filteredCriticality = new Set<string>();
  criticalityIncludeIssues = false;
  showOnlyIssuesWithDeps = false;

  issues: Issue[] = [];
  dependencyIds: number[] = [];
  lastFilter: any = undefined;

  // callbacks
  public setPosition;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(
    public dependencyDrawingService: DependencyDrawingService,
    private personService: PersonService,
    private communicationService: CommunicationService,
    private activeBoardService: ActiveBoardService,
  ) {
    this.applyFilter = this.applyFilter.bind(this);
    this.initFilters = this.initFilters.bind(this);
    this.filterIssues = this.filterIssues.bind(this);
  }

  ngOnInit() {
    this.communicationService.registerComponent(FilterWidgetComponent.componentId, this);
    this.loadData(this.initFilters);
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    switch (event.name) {
      case ComponentEvent.BOARD_CHANGED:
      case ComponentEvent.MPCBOARD_CHANGED: {
        this.loadData(this.applyFilter);
        break;
      }
      case ComponentEvent.DEPENDENCY_EDITMODE_CHANGED:
      case ComponentEvent.DEPENDENCY_VISIBILITY_CHANGED:{
        this.applyFilter();
        break;
      }
    }
  }

  applyFilter() {
    const filter = {
      status: [...this.filteredStatuses],
      assignees: [...this.filteredAssignees],
      types: [...this.filteredTypes],
      criticality: [...this.filteredCriticality],
      criticalityIncludeIssues: this.criticalityIncludeIssues,
      showOnlyIssuesWithDeps: this.showOnlyIssuesWithDeps
    };

    let filterdIssues: Issue[] = [];
    let filteredDependendencyIds: number[] = [];

    if (filter?.status) {
      filterdIssues = filterdIssues.concat(this.issues.filter(i => !filter.status.includes(i.status.id)));
    }

    if (filter?.assignees) {
      filterdIssues = filterdIssues.concat(this.issues.filter(i => !filter.assignees.includes(i.assignee?.id || '0')));
    }

    if (filter?.types) {
      filterdIssues = filterdIssues.concat(this.issues.filter(i => !filter.types.includes(i.issueType.toString())));
    }

    if (this.showOnlyIssuesWithDeps) {
      filterdIssues = filterdIssues.concat(this.issues.filter(i => !this.hasIssueDependency(i)));
    }

    if (filter?.criticality) {
      this.dependencyIds = this.getAllDependencyIds();
      if (DependencyEditmodeService.editModeActive) {
        filteredDependendencyIds = [];
      } else {
        filteredDependendencyIds = filteredDependendencyIds.concat(
          this.dependencyIds.filter(i => !this.hasDependencyCriticality(i, filter.criticality))
        );
      }
    }

    if (filter?.criticalityIncludeIssues) {
      const drawnDependenciesArray =
        <DrawnDependency[]>[].concat.apply([], Array.from(this.dependencyDrawingService.drawnDependencies.values()));
      let issuesToShow = [];
      for (let i = 0; i < drawnDependenciesArray.length; i++){
        const dependency = drawnDependenciesArray[i];
        // if the drawn dependency has a criticality which is NOT being filtered, get the source and target issues
        // note: filter.criticality includes all the criticalities which are NOT being filtered - which should be shown
        if (this.hasDependencyCriticality(dependency.issueLinkId, filter.criticality)) {
          issuesToShow = issuesToShow.concat(
            this.issues.filter(issue => issue.id === dependency.sourceIssueId || issue.id === dependency.targetIssueId)
          );
        }
      }

      const issuesToHide = this.issues.filter(i => {
        // if the issue is in the list of issues which should not be filtered, then premeptively return false
        if (issuesToShow.find(x => x.id === i.id)) {
          return false;
        }

        // otherwise check if the issue has a dependency which is included in the filtered dependency list
        const issueLinks = drawnDependenciesArray.filter(d => d.sourceIssueId === i.id || d.targetIssueId === i.id);
        return this.findOne(filteredDependendencyIds, issueLinks.map(l => l.issueLinkId));
      });

      filterdIssues = filterdIssues.concat(
        issuesToHide
      );
    }

    this.filterIssues(filterdIssues);
    if(!DependencyEditmodeService.editModeActive)
    {this.hideDependencyLines(filteredDependendencyIds, filterdIssues.map(i => i.id));}

  }

  filterIssues(filteredIssues: Issue[]) {
    const sourceIDs = filteredIssues.map(i => i.id);
    let ids = [];
    const issueHTMLElements = Array.from(document.querySelectorAll('app-issue'));
    issueHTMLElements.forEach(x => {
      const sourceId = Number(x.getAttribute('data-taskcardid'));
      if (sourceIDs.includes(sourceId)) {
        x.classList.add('hidden');
        ids = [...ids, ...this.getDependencyIdsByIssue(sourceId)].filter(i => i);
      } else {
        x.classList.remove('hidden');
      }
    });
    this.dependencyDrawingService.repaintAll();
    this.hideDependencyLines(ids, sourceIDs);
  }

  getDependencyIdsByIssue(id: number): number[] {
    return this.dependencyDrawingService.drawnDependencies.get(id)?.map(d => d.issueLinkId) || [];
  }

  shouldDependencyBeHidden(filteredDependendencyIds: number[], filteredIssueIds: number[], dependency: DrawnDependency) {
    return filteredDependendencyIds.includes(dependency.issueLinkId)
      || (filteredIssueIds.includes(dependency.sourceIssueId) || filteredIssueIds.includes(dependency.targetIssueId));

  }

  hideDependencyLines(filteredDependendencyIds: number[], filteredIssueIds: number[]): void {
    const drawnDependenciesArray =
      <DrawnDependency[]> [].concat.apply([], Array.from(this.dependencyDrawingService.drawnDependencies.values()));
    for (let i = 0; i < drawnDependenciesArray.length; i++) {
      const id = drawnDependenciesArray[i].issueLinkId;
      const drawnDependency = drawnDependenciesArray[i];
      let hideElement = false;
      if (this.shouldDependencyBeHidden(filteredDependendencyIds, filteredIssueIds, drawnDependenciesArray[i])) {
        hideElement = true;
      }

      const dependencyElements =
        Array.from(document.querySelectorAll('.' + DrawnDependency.CONNECTOR_DEPENDENCY_ID_CSS_CLASS + id));
      for (let j = 0; j < dependencyElements.length; j++) {
        const element = dependencyElements[j];
        if (hideElement) {
          element.classList.add('hidden');
          drawnDependency.line.hideOverlay(drawnDependency.sourceIssueId.toString());
        } else if (this.dependencyDrawingService.dependenciesVisible) {
          element.classList.remove('hidden');
          if(DependencyEditmodeService.editModeActive)
          {drawnDependency.line.showOverlay(drawnDependency.sourceIssueId.toString());}
        }
      }
    }
  }

  hasDependencyCriticality(dependencyId: Number, filteredCriticality: string[]): boolean {
    const dependencyLines = Array.from(document.querySelectorAll('.' + DrawnDependency.CONNECTOR_CSS_CLASS));
    const deleteButtons = Array.from(document.querySelectorAll('.' + DrawnDependency.DEPENDENCY_DELETE_CSS_CLASS));
    const htmlElements = [...dependencyLines, ...deleteButtons];
    for (let i = 0; i < htmlElements.length; i++) {
      const e = htmlElements[i];
      const classes = Array.from(e.classList);
      if (classes.includes(DrawnDependency.CONNECTOR_DEPENDENCY_ID_CSS_CLASS + dependencyId)) {
        for (let j = 0; j < filteredCriticality.length; j++) {
          if (classes.includes(Criticality[filteredCriticality[j]] + '-dependency')) {
            return true;
          }
        }
      }
    }
    return false;
  }

  hasIssueDependency(issue: Issue) {
    if (issue?.issueLinks?.length > 0) { return true; }
    const issueLinks = <IssueLink[]> [].concat.apply([], this.issues.map(i => i.issueLinks || []));
    for (const i of issueLinks) {
      console.log(i);
      if (i?.issueId === issue.id) {
        return true;
      }
    }
    return false;
  }

  ngOnDestroy(): void {
    this.resetFilter();
    this.communicationService.unregisterComponent(FilterWidgetComponent.componentId);
  }

  public getName(): string {
    return FilterWidgetComponent.widgetName;
  }

  public setUndocked(isUndocked: boolean): void {
    this.isUndocked = isUndocked;
  }

  toggleStatusFilter(statusId: number): void {
    if (!this.filteredStatuses.has(statusId)) {
      this.filteredStatuses.add(statusId);
    } else {
      this.filteredStatuses.delete(statusId);
    }
    this.applyFilter();
  }

  toggleAssigneeFilter(id: string): void {
    if (!this.filteredAssignees.has(id)) {
      this.filteredAssignees.add(id);
    } else {
      this.filteredAssignees.delete(id);
    }

    this.applyFilter();
  }

  toggleIssueTypeFilter(type): void {
    if (!this.filteredTypes.has(type)) {
      this.filteredTypes.add(type);
    } else {
      this.filteredTypes.delete(type);
    }

    this.applyFilter();
  }

  toggleshowOnlyIssuesWithDeps() {
    this.showOnlyIssuesWithDeps = !this.showOnlyIssuesWithDeps;
    this.applyFilter();
    this.dependencyDrawingService.repaintAll();
  }

  toggleCriticalityIncludeIssues() {
    this.criticalityIncludeIssues = !this.criticalityIncludeIssues;
    this.applyFilter();
    this.dependencyDrawingService.repaintAll();
  }

  toggleCriticalityFilter(criticality) {
    if (!this.filteredCriticality.has(criticality)) {
      this.filteredCriticality.add(criticality);
    } else {
      this.filteredCriticality.delete(criticality);
    }

    this.applyFilter();
  }

  resetFilter(): void {
    this.filteredStatuses = new Set(this.statuses.map(s => s.id));
    this.filteredAssignees = new Set(Array.from(this.members.values()).map(s => '' + s.id));
    this.filteredTypes = new Set(Object.keys(this.issueTypes));
    this.showOnlyIssuesWithDeps = false;
    this.filteredCriticality = new Set<string>(Object.keys(Criticality));
    this.criticalityIncludeIssues = false;
    this.applyFilter();
  }

  toggleDependencies(): void {
    this.dependencyDrawingService.setShowDependencies(!this.dependencyDrawingService.showDependencies);
    this.applyFilter();
  }

  loadData(callBack?: Function): void {
    this.activeBoardService.getBoardData().subscribe(data => {
      this.issues = data.issues;
      this.statuses = data.statuses;

      const unassigned = new Person('0', 'Unassigned', 'Unassigned', '', '', '', '');
      this.members.set('0', unassigned);
      this.loadMembers(data.projectKeys, callBack);
    });
  }

  loadMembers(projectsKeys: string[], callBack?: Function): void {
    const allMemberObservables: Observable<Person[]>[] = [];
    for (const projectKey of projectsKeys) {
      const members = this.personService.getMembers(projectKey);
      allMemberObservables.push(members);
    }
    merge(
      allMemberObservables
    ).subscribe(mergedSubscription =>
      mergedSubscription.subscribe((members) => {
        for (const member of members) {
          this.members.set(member.id, member);
        }
        if(callBack)
        {callBack();}
      }));
  }

  private getAllDependencyIds(): number[] {
    const ids = new Set<number>();
    const dependencyLines = Array.from(document.querySelectorAll('.' + DrawnDependency.CONNECTOR_CSS_CLASS));
    const htmlElements = [...dependencyLines];
    if (htmlElements.length === 0) { return []; }
    for (let i = 0; i < htmlElements.length; i++) {
      const element = htmlElements[i];
      for (let j = 0; j < element.classList.length; j++) {
        if (element.classList[j].startsWith(DrawnDependency.CONNECTOR_DEPENDENCY_ID_CSS_CLASS)) {
          const id = Number(('' + element.classList[j]).replace(DrawnDependency.CONNECTOR_DEPENDENCY_ID_CSS_CLASS, '')).valueOf();
          ids.add(id);
        }
      }
    }
    return Array.from(ids);
  }

  private initFilters(): void {
    this.filteredStatuses = new Set(Array.from(this.statuses.values()).map(s => s.id));
    this.filteredAssignees = new Set(Array.from(this.members.values()).map(a => '' + a.id));
    this.filteredTypes = new Set<string>(Object.keys(this.issueTypes));
    this.filteredCriticality = new Set<string>(Object.keys(Criticality));
  }

  /**
   * https://stackoverflow.com/questions/16312528/check-if-an-array-contains-any-element-of-another-array-in-javascript/29447130
   * @description determine if an array contains one or more items from another array.
   * @param {array} haystack the array to search.
   * @param {array} arr the array providing items to check for in the haystack.
   * @return {boolean} true|false if haystack contains at least one item from arr.
  */
  private findOne(haystack, arr): Boolean {
    return arr.some(function (v) {
      return haystack.indexOf(v) >= 0;
    });
  };

}
